import React, { useCallback, useMemo, useContext, useState } from 'react';
import moment from 'moment';
import { withTranslation, Trans } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';

import { activateUserDetailed } from 'actions/app-props';

import { updateCurrentUserStatus, updateCurrentUser } from 'actions/users';

import { startConversation } from 'services/chat-service';
import { trackEvent } from 'services/events-tracker-service';
import { createAlert } from 'services/alert-service';

import { useCurrentUserId } from 'shared-hooks/user';
import { useCanUser } from 'shared-hooks/permission';

import { RolesAndPermissionsContext } from 'context/RolesAndPermissionsContext';
import { WebexContext } from 'context/WebexContext';

import AvatarBase from 'components/avatar-base';
import DateTime from 'components/DateTime';
import UserChatButton from 'components/UserChatButton';
import UserLabels from 'components/UserLabels';
import FieldAvatar from 'components/field-avatar';
import Tooltip from 'components/tooltip';

import IconSettings from 'icons/icon-settings';

const UserCard = ({ image, color, secondaryColor, secondary_color, initials, name, online, available, onSubtitleClick, subtitleIcon, additional, email, subtitle, timezone, removed, role, id, pending, chatDisabled, onUserProfileClick, t }) => {
    const rolesAndPermissions = useContext(RolesAndPermissionsContext);
    const webexCtx = useContext(WebexContext);
    const currentUserId = useCurrentUserId();
    const canViewMemberEmail = useCanUser('viewMemberEmail');
    const history = useHistory();
    const location = useLocation();
    const [avatarState, setAvatarState] = useState(null);
    const [uploading, setUploading] = useState(false);

    const uploadAvatar = async fileObject => {
        if (!fileObject) {
            return new Promise(res => res());
        }

        trackEvent('USER.AVATAR_UPLOAD');

        await updateCurrentUser({
            avatar: fileObject.file,
            avatarBase64: fileObject.base64
        });

        createAlert('success', t('alerts.avatarUpdated'), {
            duration: 3000
        });
    }

    const handleSubtitleClick = useCallback(() => {
        onSubtitleClick(id);
    }, [id, onSubtitleClick]);

    const handleUserProfileClick = useCallback(() => {
        activateUserDetailed(id);

        onUserProfileClick && onUserProfileClick();
    }, [id, onUserProfileClick]);

    const handleStartConversationClick = useCallback(() => {
        startConversation(id);
    }, [id]);

    const handleStatusToggleClick = useCallback(() => {
        updateCurrentUserStatus(available === false ? true : false);
    }, [available]);

    const handleSettingsClick = useCallback(() => {
        history.push({
            pathname: '/settings/profile',
            state: {
                closeUrl: location.pathname
            }
        });

        onUserProfileClick && onUserProfileClick();
    }, [history, location, onUserProfileClick]);

    const handleAvatarChange = ({ fileObject }) => {
        uploadAvatar(fileObject);
        setAvatarState(fileObject.base64);
    }

    const isSelf = useMemo(() => {
        return currentUserId === id;
    }, [currentUserId, id]);

    const roleElement = useMemo(() => (
        <React.Fragment>
            { rolesAndPermissions?.getRoleLabel(role) }

            { pending ? ` (${t('common:pending')})` : '' }
        </React.Fragment>
    ), [role, pending, rolesAndPermissions?.getRoleLabel]);

    return (
        <div className="popout-inner">
            <div className="user-card">
                <div className="card-body">
                    <div className="card-body-side">
                        {
                            // isSelf ? (
                            false ? (
                                <Tooltip tooltip={(avatarState || image) ? t('settings:Upload a new avatar') : t('settings:Upload a avatar')} placement="top" withTooltipHover={false} showDelay={200}>
                                    <div>
                                        <FieldAvatar
                                            size={'xlarge'}
                                            image={avatarState || image}
                                            color={color}
                                            secondaryColor={secondaryColor || secondary_color}
                                            text={initials}
                                            onChange={handleAvatarChange}
                                        />
                                    </div>
                                </Tooltip>
                            ) : (
                                <AvatarBase
                                    image={image}
                                    color={color}
                                    foregroundColor={secondaryColor || secondary_color}
                                    text={initials}
                                    size={'xlarge'}
                                />
                            )
                        }
                    </div>

                    <div className="card-body-main">
                        <h5><span>{name}</span> <i className={'status-indicator' + (online ? ' active' : '')} /></h5>

                        {
                            role && !subtitle ? (
                                <h6>{ roleElement }</h6>
                            ) : null
                        }

                        {
                            subtitle ? <h6 className={'subtitle' + (onSubtitleClick ? ' action' : '')} onClick={onSubtitleClick && handleSubtitleClick}>{subtitle} {subtitleIcon}</h6> : null
                        }

                        {
                            additional ? <h6 className="additional">{additional}</h6> : null
                        }

                        {
                            !subtitle && !additional ? (
                                <div className="labels-holder">
                                    <UserLabels id={id} />
                                </div>
                            ) : null
                        }
                    </div>
                </div>

                {
                    isSelf && !webexCtx?.inApp ? (
                        <div className="card-content">
                            <button className="btn btn-secondary btn-block" onClick={handleStatusToggleClick}>
                                <Trans
                                    i18nKey="user:setStatus"
                                    values={{
                                        status: available === false ? t('states.active') : t('states.away')
                                    }}
                                    components={{
                                        strong: <strong style={{ margin: '0 4px' }} />
                                    }}
                                />
                            </button>
                        </div>
                    ) : null
                }

                <div className="card-foot">
                    {
                        (canViewMemberEmail || isSelf) && role !== 'client' ? (
                            <div className="card-meta">
                                <i className="icon material-icons">email</i>

                                { email }
                            </div>
                        ) : null
                    }

                    {
                        role && subtitle ? (
                            <div className="card-meta">
                                <i className="icon material-icons">person</i>

                                { roleElement }
                            </div>
                        ) : null
                    }

                    {
                        timezone ? (
                            <div className="card-meta">
                                <i className="icon material-icons">access_time</i>

                                <span>{<DateTime format="time">{moment().utcOffset((timezone || 0) * -1)}</DateTime>} ({t('date:local time')})</span>
                            </div>
                        ) : null
                    }

                    {
                        !removed && !webexCtx?.inApp ? (
                            <React.Fragment>
                                <div className="card-cta">
                                    <button className="btn btn-block btn-xsmall btn-secondary" onClick={handleUserProfileClick}>
                                        <i className="icon material-icons">person</i>

                                        { t('See profile') }
                                    </button>

                                    <UserChatButton id={id} role={role} pending={pending}>
                                        <button className="btn btn-block btn-xsmall btn-secondary" onClick={handleStartConversationClick}>
                                            <i className="icon ion ion-chatboxes" />

                                            { t('chat:Send message') }
                                        </button>
                                    </UserChatButton>
                                </div>

                                {
                                    isSelf ? (
                                        <div className="card-cta">
                                            <button className="btn btn-block btn-xsmall btn-secondary" onClick={handleSettingsClick}>
                                                <IconSettings />

                                                { t('settings:Profile Settings') }
                                            </button>
                                        </div>
                                    ) : null
                                }
                            </React.Fragment>
                        ) : null
                    }
                </div>
            </div>
        </div>
    )

}

export default withTranslation(['user', 'common', 'date', 'chat', 'settings'])(UserCard);
